"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const tslib_1 = require("tslib");
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = tslib_1.__importStar(require("react"));
const react_dom_1 = tslib_1.__importDefault(require("react-dom"));
const prop_types_1 = tslib_1.__importDefault(require("prop-types"));
const classnames_1 = tslib_1.__importDefault(require("classnames"));
const activeElement_1 = tslib_1.__importDefault(require("dom-helpers/activeElement"));
const react_transition_group_1 = require("react-transition-group");
const portalRoot_1 = require("../../utils/portalRoot");
const useEsc_1 = tslib_1.__importDefault(require("../../hooks/useEsc"));
const useAfterMount_1 = tslib_1.__importDefault(require("../../hooks/useAfterMount"));
const DialogHeader_1 = tslib_1.__importDefault(require("./DialogHeader"));
const DialogBody_1 = tslib_1.__importDefault(require("./DialogBody"));
const DialogFooter_1 = tslib_1.__importDefault(require("./DialogFooter"));
const baseDialogPropTypes_1 = tslib_1.__importDefault(require("./baseDialogPropTypes"));
const useFocusTrap_1 = tslib_1.__importDefault(require("../../hooks/useFocusTrap"));
const MODAL_DIALOG_CLASS = 'modal-dialog';
const MODAL_OPEN_CLASS = 'modal-open';
const Dialog = props => {
    const { title, subtitle, body, footer, headerButtons, className, bodyClassName, footerClassName, showCloseButton, useOverflow, bsSize, show, onHide, disableEsc, onEsc, onCloseValidation } = props, remainingProps = tslib_1.__rest(props, ["title", "subtitle", "body", "footer", "headerButtons", "className", "bodyClassName", "footerClassName", "showCloseButton", "useOverflow", "bsSize", "show", "onHide", "disableEsc", "onEsc", "onCloseValidation"]);
    const [open, setOpen] = (0, react_1.useState)(show);
    const dialogWrapperRef = (0, react_1.useRef)();
    const modalRoot = (0, portalRoot_1.getOrCreatePortalRoot)();
    // Use a setter function for the focus trap as it would nor re-render
    // and fails to set the focus listener
    const [focusTrapRef, setFocusTrapRef] = (0, react_1.useState)();
    (0, useFocusTrap_1.default)(focusTrapRef);
    const toggleBodyClass = add => {
        // We need to set a body class to fix the -webkit-overflow-scrolling on safari and iOS
        // Remove "modal-open" from body only when there is no other dialog in the DOM
        // in order to support multiple dialogs
        const hasOtherDialogs = modalRoot.getElementsByClassName(MODAL_DIALOG_CLASS).length > 1;
        if (add) {
            document.body.classList.add(MODAL_OPEN_CLASS);
        }
        else if (!add && !hasOtherDialogs) {
            document.body.classList.remove(MODAL_OPEN_CLASS);
        }
    };
    const handleClose = usedEscapeKey => {
        if (onCloseValidation()) {
            setOpen(false);
            onHide();
            usedEscapeKey && onEsc();
        }
    };
    (0, useAfterMount_1.default)(() => {
        setOpen(show);
        toggleBodyClass(show);
    }, [show]);
    (0, useEsc_1.default)(() => {
        if (!dialogWrapperRef || !dialogWrapperRef.current) {
            return;
        }
        const dialogElement = dialogWrapperRef.current;
        const currentActiveElement = (0, activeElement_1.default)(document);
        // Only allow to close the dialog when the focus is inside the dialog
        if (!disableEsc && dialogElement.contains(currentActiveElement)) {
            handleClose(true);
        }
    });
    const handleEntered = () => {
        var _a;
        const dialogElement = dialogWrapperRef.current;
        const currentActiveElement = (0, activeElement_1.default)(document);
        // Set the focus to the dialog if no element inside has focus already. Otherwise, focused
        // elements like inputs would loos their focus to the dialog.
        // Note that in order to focus the dialog itself, the tabindex has to be set on that element
        if (!dialogElement.contains(currentActiveElement)) {
            (_a = dialogWrapperRef === null || dialogWrapperRef === void 0 ? void 0 : dialogWrapperRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
    };
    const modalClasses = (0, classnames_1.default)('modal', 'show', className);
    const modalDialogClasses = (0, classnames_1.default)(MODAL_DIALOG_CLASS, useOverflow && 'modal-overflow', bsSize === Dialog.SIZE_SM && 'modal-sm', bsSize === Dialog.SIZE_LG && 'modal-lg', bsSize === Dialog.SIZE_XL && 'modal-xl', bsSize === Dialog.SIZE_FULL && 'modal-full-width', bsSize === Dialog.SIZE_FULL_WIDTH && 'modal-full-width', bsSize === Dialog.SIZE_FULL_HEIGHT && 'modal-full-height', bsSize === Dialog.SIZE_FULL_SCREEN && 'modal-fullscreen');
    return react_dom_1.default.createPortal((0, jsx_runtime_1.jsx)(react_transition_group_1.CSSTransition, Object.assign({ in: open, timeout: 200, classNames: 'modal', unmountOnExit: true, onEntered: handleEntered }, { children: (0, jsx_runtime_1.jsxs)("div", Object.assign({}, remainingProps, { className: modalClasses, tabIndex: 0, role: 'dialog', ref: dialogWrapperRef }, { children: [(0, jsx_runtime_1.jsx)("div", Object.assign({ ref: setFocusTrapRef, className: modalDialogClasses, role: 'document' }, { children: (0, jsx_runtime_1.jsxs)("div", Object.assign({ className: 'modal-content' }, { children: [title && ((0, jsx_runtime_1.jsx)(DialogHeader_1.default, { title: title, subtitle: subtitle, headerButtons: headerButtons, showCloseButton: showCloseButton, onClose: handleClose })), body && (0, jsx_runtime_1.jsx)(DialogBody_1.default, Object.assign({ className: bodyClassName }, { children: body })), footer && (0, jsx_runtime_1.jsx)(DialogFooter_1.default, Object.assign({ className: footerClassName }, { children: footer }))] })) })), (0, jsx_runtime_1.jsx)("div", { className: 'modal-backdrop' })] })) })), modalRoot);
};
Dialog.SIZE_SM = 'sm';
Dialog.SIZE_MD = 'md'; // default
Dialog.SIZE_LG = 'lg';
Dialog.SIZE_XL = 'xl';
Dialog.SIZE_FULL = 'full';
Dialog.SIZE_FULL_WIDTH = 'fullwidth';
Dialog.SIZE_FULL_HEIGHT = 'fullheight';
Dialog.SIZE_FULL_SCREEN = 'fullscreen';
Dialog.defaultProps = {
    show: false,
    onHide: () => { },
    onEsc: () => { },
    className: '',
    showCloseButton: true,
    disableEsc: false,
    useOverflow: false,
    onCloseValidation: () => true,
};
Dialog.propTypes = Object.assign(Object.assign({}, baseDialogPropTypes_1.default), { body: prop_types_1.default.node, footer: prop_types_1.default.node, headerButtons: prop_types_1.default.node, className: prop_types_1.default.string, bodyClassName: prop_types_1.default.string, footerClassName: prop_types_1.default.string, onHide: prop_types_1.default.func, onEsc: prop_types_1.default.func });
exports.default = Dialog;
